"use client"

import { useEffect, useId, useMemo, useRef, useState } from "react"
import type { ProjectMilestoneFormField } from "@/schema/project-template"
import { zodResolver } from "@hookform/resolvers/zod"
import { BookSelectInput } from "@ui/book-select"
import { AssignmentSelectField } from "@ui/forms/fields/assignment-select-field"
import { BusinessSelectorField } from "@ui/forms/fields/business-select-field"
import { ComboboxField } from "@ui/forms/fields/combobox-field"
import { ContactSelectorField } from "@ui/forms/fields/contact-select-field"
import { HouseholdSelectorField } from "@ui/forms/fields/household-select-field"
import { TextField } from "@ui/forms/fields/text-field"
import { Form, FormField } from "@ui/shad"
import { format } from "date-fns"
import { BookOpen } from "lucide-react"
import { useForm, useWatch } from "react-hook-form"
import { toast } from "sonner"
import { z } from "zod"

import { bookShortName, isAllBooksId } from "@/lib/all-books"
import { useExecuteActionLink } from "@/hooks/api/action-links"
import { useFetchHousehold } from "@/hooks/api/households/queries"
import { useFetchProjectTemplates } from "@/hooks/api/project-templates/queries"
import { useCreateProject } from "@/hooks/api/projects"
import { useFetchCurrentUser } from "@/hooks/api/users"
import { useBookId } from "@/hooks/use-book-id"
import { useLoadingRouter } from "@/hooks/use-loading-router"
import { GridFormDialog, SimpleFormDialog } from "@/components/dialogs"
import { findMissingRequiredFields } from "@/components/projects/form-field-response-helpers"
import { MilestoneFormFieldsInput } from "@/components/projects/milestone-form-fields-input"

import { DateField } from "../_ui/forms/fields/date-field"

const projectFormSchema = z.object({
  name: z.string().optional(),
  household_id: z.string().optional(),
  contact_id: z.string().optional(),
  business_id: z.string().optional(),
  project_template_id: z.string().min(1, "Project type is required"),
  assignment_id: z.string().min(1, "Assignee is required"),
  due_date: z.date().optional(),
})

type ProjectFormValues = z.infer<typeof projectFormSchema>

interface ProjectCreationOptions {
  templateId?: string
  householdId?: string
  contactId?: string
  businessId?: string
  bookId?: string
  assignedToId?: string
  assignedTeamId?: string
  projectName?: string
  initialFormResponses?: Record<string, string | number | boolean | null>
  actionLinkId?: string
  lockTemplate?: boolean
}

interface ProjectCreationFormProps extends ProjectCreationOptions {
  onClose: () => void
}

export function ProjectCreationForm({
  onClose,
  templateId,
  householdId,
  contactId,
  businessId,
  bookId,
  assignedToId,
  assignedTeamId,
  projectName,
  initialFormResponses,
  actionLinkId,
  lockTemplate = false,
}: ProjectCreationFormProps) {
  const formId = useId()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const routeBookId = useBookId()
  const showBookSelector = !bookId && isAllBooksId(routeBookId)
  const [selectedBookId, setSelectedBookId] = useState<string | null>(bookId ?? null)
  const [scopedHouseholdId, setScopedHouseholdId] = useState(householdId)
  const [scopedContactId, setScopedContactId] = useState(contactId)
  const [scopedBusinessId, setScopedBusinessId] = useState(businessId)
  const effectiveBookId = bookId ?? selectedBookId ?? undefined
  const [formResponses, setFormResponses] = useState<Record<string, string | number | boolean | null>>(
    initialFormResponses ?? {}
  )
  const router = useLoadingRouter()

  const { data: projectTemplatesResponse, isLoading: loadingTemplates } = useFetchProjectTemplates(effectiveBookId)
  const { data: currentUser } = useFetchCurrentUser()
  const createProject = useCreateProject(effectiveBookId)
  const executeActionLink = useExecuteActionLink()

  const form = useForm<ProjectFormValues>({
    resolver: zodResolver(projectFormSchema),
    defaultValues: {
      name: projectName || "",
      household_id: scopedHouseholdId || "",
      contact_id: scopedContactId || "",
      business_id: scopedBusinessId || "",
      project_template_id: templateId || "",
      assignment_id: assignedTeamId
        ? `team:${assignedTeamId}`
        : assignedToId || currentUser?.id
          ? `user:${assignedToId ?? currentUser?.id}`
          : "",
    },
  })

  useEffect(() => {
    const defaultAssigneeId = assignedToId ?? currentUser?.id
    if (defaultAssigneeId && !assignedTeamId && !form.getValues("assignment_id")) {
      form.setValue("assignment_id", `user:${defaultAssigneeId}`, { shouldDirty: false })
    }
  }, [assignedTeamId, assignedToId, currentUser, form])

  const selectedBook = effectiveBookId ? currentUser?.books?.find((b) => b.id === effectiveBookId) : undefined
  const bookName = selectedBook && currentUser?.books ? bookShortName(selectedBook, currentUser.books) : undefined
  const isBookReady = !showBookSelector || !!selectedBookId
  const sortedTemplateOptions = useMemo(
    () =>
      (projectTemplatesResponse?.project_templates || [])
        .filter((template) => {
          if (scopedHouseholdId) return template.projectable_kind === "household"
          if (scopedContactId) return template.projectable_kind === "contact"
          if (scopedBusinessId) return template.projectable_kind === "business"
          return true
        })
        .map((template) => ({ value: template.id, label: template.title }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [projectTemplatesResponse?.project_templates, scopedBusinessId, scopedContactId, scopedHouseholdId]
  )

  const selectedTemplateId = form.watch("project_template_id")
  const previousTemplateId = useRef(selectedTemplateId)
  const selectedTemplate = projectTemplatesResponse?.project_templates?.find((t) => t.id === selectedTemplateId)
  const isContactTemplate = selectedTemplate?.projectable_kind === "contact"
  const isBusinessTemplate = selectedTemplate?.projectable_kind === "business"
  const watchedHouseholdId = useWatch({ control: form.control, name: "household_id" })
  const activeHouseholdId =
    isContactTemplate || isBusinessTemplate ? null : watchedHouseholdId || scopedHouseholdId || null
  const previousHouseholdId = useRef(activeHouseholdId)
  const { data: activeHousehold } = useFetchHousehold(activeHouseholdId ?? "", { enabled: !!activeHouseholdId })
  const firstMilestoneFormFields: ProjectMilestoneFormField[] =
    selectedTemplate?.project_milestones?.[0]?.project_milestone_form_fields?.filter(
      (field) =>
        (!isContactTemplate && !isBusinessTemplate) ||
        !["household_record", "file_attachment"].includes(field.field_type)
    ) ?? []
  const hasMilestoneFormFields = firstMilestoneFormFields.length > 0

  useEffect(() => {
    if (previousTemplateId.current === selectedTemplateId) return

    previousTemplateId.current = selectedTemplateId
    setFormResponses({})
  }, [selectedTemplateId])

  useEffect(() => {
    if (previousHouseholdId.current === activeHouseholdId) return

    previousHouseholdId.current = activeHouseholdId
    setFormResponses({})
  }, [activeHouseholdId])

  const handleBookChange = (value: string | null) => {
    const isChangingBook = selectedBookId !== null && selectedBookId !== value
    setSelectedBookId(value)
    if (!isChangingBook) return

    setScopedHouseholdId(undefined)
    setScopedContactId(undefined)
    setScopedBusinessId(undefined)
    form.reset({
      ...form.getValues(),
      household_id: "",
      contact_id: "",
      business_id: "",
      project_template_id: "",
      assignment_id: currentUser?.id ? `user:${currentUser.id}` : "",
    })
    setFormResponses({})
  }

  const handleSubmit = async (values: ProjectFormValues) => {
    if (isSubmitting || (showBookSelector && !selectedBookId)) return

    const selectedRelatedRecordId = isContactTemplate
      ? values.contact_id
      : isBusinessTemplate
        ? values.business_id
        : values.household_id

    if (!selectedRelatedRecordId && !values.name?.trim()) {
      form.setError("name", { message: "Project name is required" })
      return
    }

    const missingRequired = findMissingRequiredFields(
      firstMilestoneFormFields,
      formResponses,
      isContactTemplate || isBusinessTemplate ? null : (values.household_id ?? null)
    )
    if (missingRequired.length > 0) {
      toast.error(`${missingRequired[0].label} is required`)
      return
    }

    setIsSubmitting(true)
    const { assignment_id, business_id, contact_id, household_id, due_date, ...rest } = values
    const assignedToId = assignment_id.startsWith("user:") ? assignment_id.slice(5) : null
    const assignedTeamId = assignment_id.startsWith("team:") ? assignment_id.slice(5) : null

    const payload = {
      ...rest,
      name: values.name?.trim() || undefined,
      assigned_to_id: assignedToId,
      assigned_team_id: assignedTeamId,
      household_id: isContactTemplate || isBusinessTemplate ? undefined : household_id || undefined,
      due_date: due_date ? format(due_date, "yyyy-MM-dd") : undefined,
      projectable_type:
        isContactTemplate && contact_id
          ? ("Contact" as const)
          : isBusinessTemplate && business_id
            ? ("Business" as const)
            : undefined,
      projectable_id: isContactTemplate
        ? contact_id || undefined
        : isBusinessTemplate
          ? business_id || undefined
          : undefined,
      ...(Object.keys(formResponses).length > 0 && { form_responses: formResponses }),
    }
    try {
      let projectId: string | undefined
      if (actionLinkId) {
        const result = await executeActionLink.mutateAsync({
          id: actionLinkId,
          input: {
            name: payload.name,
            assigned_to_id: payload.assigned_to_id,
            assigned_team_id: payload.assigned_team_id,
            due_date: payload.due_date,
            form_responses: payload.form_responses,
          },
        })
        projectId = typeof result.project_id === "string" ? result.project_id : undefined
      } else {
        const project = await createProject.mutateAsync(payload)
        projectId = project.id
      }
      form.reset()
      setFormResponses({})
      onClose()
      toast.success("Project created", {
        ...(projectId && {
          action: {
            label: "View project",
            onClick: () => router.push(`/projects/${projectId}`),
          },
        }),
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      form.reset()
      onClose()
    }
  }

  if (templateId && loadingTemplates) return null

  const ProjectFormDialog = hasMilestoneFormFields ? GridFormDialog : SimpleFormDialog

  return (
    <ProjectFormDialog
      open
      onOpenChange={(open) => {
        if (!open) handleClose()
      }}
      title={"Start new project"}
      onClose={handleClose}
      action={{
        label: "Start new project",
        form: formId,
        disabled: isSubmitting || !isBookReady,
        pending: isSubmitting,
      }}
      cancel={{ label: "Cancel", onClick: handleClose, disabled: isSubmitting }}
    >
      <Form {...form}>
        <form id={formId} onSubmit={form.handleSubmit(handleSubmit)} className="flex min-h-0 flex-1 flex-col gap-2">
          {showBookSelector && (
            <div className="space-y-1">
              <label htmlFor="project-book" className="text-sm font-medium">
                Book
              </label>
              <BookSelectInput
                id="project-book"
                value={selectedBookId}
                onChange={handleBookChange}
                placeholder="Select a book"
                size="default"
                className="w-full"
              />
            </div>
          )}
          {!showBookSelector && bookName && (
            <div className="flex items-center gap-2">
              <BookOpen className="text-muted-foreground size-4 shrink-0" />
              <span className="text-sm">{bookName}</span>
            </div>
          )}
          {hasMilestoneFormFields ? (
            <div className="grid gap-4 md:grid-cols-[0.7fr_1fr] [&>*]:min-w-0">
              <div className="space-y-3">
                <FormField
                  control={form.control}
                  name="project_template_id"
                  render={({ field }) => (
                    <ComboboxField
                      field={field}
                      id="project-template"
                      label="Project type"
                      placeholder="Search project types..."
                      disabled={loadingTemplates || lockTemplate || !isBookReady}
                      options={loadingTemplates ? [] : sortedTemplateOptions}
                      emptyText="No project types found."
                    />
                  )}
                />

                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <TextField field={field} id="project-name" label="Project name" placeholder="Enter project name" />
                  )}
                />

                {!scopedHouseholdId && selectedTemplate && !isContactTemplate && !isBusinessTemplate && (
                  <FormField
                    control={form.control}
                    name="household_id"
                    render={({ field }) => (
                      <HouseholdSelectorField field={field} label="Household" bookIdOverride={effectiveBookId} />
                    )}
                  />
                )}

                {!scopedContactId && selectedTemplate && isContactTemplate && (
                  <FormField
                    control={form.control}
                    name="contact_id"
                    render={({ field }) => <ContactSelectorField field={field} bookIdOverride={effectiveBookId} />}
                  />
                )}

                {!scopedBusinessId && selectedTemplate && isBusinessTemplate && (
                  <FormField
                    control={form.control}
                    name="business_id"
                    render={({ field }) => <BusinessSelectorField field={field} bookIdOverride={effectiveBookId} />}
                  />
                )}

                <FormField
                  control={form.control}
                  name="assignment_id"
                  render={({ field }) => (
                    <AssignmentSelectField id="project-assigned-to" field={field} bookId={effectiveBookId} />
                  )}
                />

                <FormField
                  control={form.control}
                  name="due_date"
                  render={({ field }) => (
                    <DateField field={field} id="project-due-date" label="Due date" placeholder="Select due date" />
                  )}
                />
              </div>

              <div className="border-border space-y-2 rounded-md border p-3">
                <p className="text-muted-foreground text-xs font-medium">
                  Form fields for {selectedTemplate?.project_milestones?.[0]?.title ?? "First milestone"}
                </p>
                <MilestoneFormFieldsInput
                  fields={firstMilestoneFormFields}
                  values={formResponses}
                  onChange={setFormResponses}
                  disabled={isSubmitting}
                  householdRecordScope={{ householdId: activeHouseholdId, bookId: effectiveBookId ?? null }}
                  fileAttachmentScope={{
                    householdId: activeHouseholdId ?? null,
                    projectTemplateId: selectedTemplate?.id ?? null,
                    hasStorageFolder: !!activeHousehold?.storage_folder_id,
                  }}
                />
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <FormField
                control={form.control}
                name="project_template_id"
                render={({ field }) => (
                  <ComboboxField
                    field={field}
                    id="project-template"
                    label="Project type"
                    placeholder="Search project types..."
                    disabled={loadingTemplates || lockTemplate || !isBookReady}
                    options={loadingTemplates ? [] : sortedTemplateOptions}
                    emptyText="No project types found."
                  />
                )}
              />

              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <TextField field={field} id="project-name" label="Project name" placeholder="Enter project name" />
                )}
              />

              {!scopedHouseholdId && selectedTemplate && !isContactTemplate && !isBusinessTemplate && (
                <FormField
                  control={form.control}
                  name="household_id"
                  render={({ field }) => (
                    <HouseholdSelectorField field={field} label="Household" bookIdOverride={effectiveBookId} />
                  )}
                />
              )}

              {!scopedContactId && selectedTemplate && isContactTemplate && (
                <FormField
                  control={form.control}
                  name="contact_id"
                  render={({ field }) => <ContactSelectorField field={field} bookIdOverride={effectiveBookId} />}
                />
              )}

              {!scopedBusinessId && selectedTemplate && isBusinessTemplate && (
                <FormField
                  control={form.control}
                  name="business_id"
                  render={({ field }) => <BusinessSelectorField field={field} bookIdOverride={effectiveBookId} />}
                />
              )}

              <FormField
                control={form.control}
                name="assignment_id"
                render={({ field }) => (
                  <AssignmentSelectField id="project-assigned-to" field={field} bookId={effectiveBookId} />
                )}
              />

              <FormField
                control={form.control}
                name="due_date"
                render={({ field }) => (
                  <DateField field={field} id="project-due-date" label="Due date" placeholder="Select due date" />
                )}
              />
            </div>
          )}
        </form>
      </Form>
    </ProjectFormDialog>
  )
}

export function useCreateProjectDialog() {
  const [options, setOptions] = useState<ProjectCreationOptions | null>(null)
  const close = () => setOptions(null)
  return {
    open: (options: ProjectCreationOptions = {}) => setOptions(options),
    close,
    dialog: options ? <ProjectCreationForm {...options} onClose={close} /> : null,
  }
}
